import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/mobile-website-mistakes");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/mobile-website-mistakes")({
  head: () => ({
    meta: [
        { name: "description", content: "Seven mobile website mistakes silently costing Colorado service businesses phone calls. Fix these before your next ad dollar goes to waste." },
      {
        title: "7 Mobile Website Mistakes Costing Colorado Calls",
      },
      {
        name: "description",
        content:
          "70% of your traffic is on a phone. The 7 mobile mistakes we see on almost every Colorado contractor and service site — and the exact fixes.",
      },
      {
        property: "og:title",
        content: "7 Mobile Website Mistakes (Colorado Service Businesses)",
      },
      {
        property: "og:description",
        content:
          "Mobile-first fixes that move the needle on real phone calls.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/mobile-website-mistakes` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/mobile-website-mistakes" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Web Design"
      read="8 min read"
      title="7 mobile website mistakes costing Colorado service businesses calls"
      intro="Roughly 7 out of 10 visitors to a Colorado home-service website are on a phone. Get the mobile experience wrong and the rest of your marketing budget is leaking out the bottom of a bucket."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Mobile mistakes" },
      ]}
      path="/learn/mobile-website-mistakes"
      faqs={[
        { q: "What is the biggest mobile website mistake?", a: "Slow load times. If your site takes more than 3 seconds to load on a phone, more than half of visitors leave before they even see it." },
        { q: "Does Google rank mobile sites differently than desktop?", a: "Yes — Google has used mobile-first indexing since 2021. The mobile version of your site is what Google ranks. If it's worse than your desktop version, you're losing rankings." },
        { q: "Should I use a separate mobile site or responsive design?", a: "Always responsive. Separate mobile sites (m.yoursite.com) are outdated, harder to maintain, and confuse Google. One responsive site that adapts to any screen size is the standard." },
      ]}
    >
      <h2>1. The phone number is just text, not a tap-to-call link</h2>
      <p>
        Wrap it in <code>&lt;a href=&quot;tel:...&quot;&gt;</code>. It costs zero to fix and
        recovers calls every day.
      </p>

      <h2>2. Tiny tap targets</h2>
      <p>
        Buttons under 44×44 pixels are misery on a phone. Make every CTA a fat thumb-friendly
        button.
      </p>

      <h2>3. Pop-ups that block content within 2 seconds</h2>
      <p>
        Google penalizes intrusive interstitials on mobile. So do users. If you have to use one,
        delay it 30+ seconds and make the close button huge.
      </p>

      <h2>4. The hero is a 4MB image</h2>
      <p>
        That &ldquo;beautiful&rdquo; hero photo your designer loves is murdering your load time on
        Colorado&rsquo;s rural cellular. Compress, lazy-load, and use modern formats (WebP/AVIF).
      </p>

      <h2>5. Sticky headers eating half the screen</h2>
      <p>
        A sticky header is fine. A sticky header that&rsquo;s 120px tall on a 700px screen is not.
        Keep it under 70px and let the user breathe.
      </p>

      <h2>6. Forms without proper input types</h2>
      <p>
        Use <code>type=&quot;tel&quot;</code> for phone, <code>type=&quot;email&quot;</code> for
        email, <code>autocomplete=&quot;name&quot;</code> for name. The phone keyboard popping up
        for a phone field is a small detail that meaningfully lifts completion rate.
      </p>

      <h2>7. No sticky &ldquo;Call now&rdquo; bar on long pages</h2>
      <p>
        On any service page longer than a single screen, add a thin sticky bottom bar with
        &ldquo;Call now&rdquo; and the number. This single change can lift mobile call rate by
        20–40%.
      </p>

      <h2>How to test in 5 minutes</h2>
      <ol>
        <li>Open your site on your phone over <strong>cellular</strong>, not Wi-Fi.</li>
        <li>Tap the phone number in the header. Does it dial?</li>
        <li>Time how long the hero takes to be readable. Over 3 seconds = fix.</li>
        <li>Try to fill the contact form one-handed. Is anything painful?</li>
        <li>Scroll to the bottom of a service page. Can you call without scrolling back to the top?</li>
      </ol>

      <h2>The bigger picture</h2>
      <p>
        These 7 fixes are the easiest wins. The next layer is page structure, copy, and proof —
        covered in{" "}
        <Link to="/learn/website-conversion" className="font-bold text-brand-gold-deep underline">
          Why your website gets traffic but no calls
        </Link>
        . If you want all of it handled in-house by our Colorado team, that&rsquo;s exactly what
        our{" "}
        <Link to="/colorado-springs-web-design" className="font-bold text-brand-gold-deep underline">
          Web Design service
        </Link>{" "}
        does.
      </p>
    </ArticleLayout>
  );
}
